// frontend/src/components/onboarding/compare-runs-page.tsx
//
// Side-by-side comparison view between two measurement runs.
//
// Fetched via GET /onboarding/customers/{id}/runs/compare/data?baseline=A&candidate=B
//
// Layout:
//   - Top: back button + brand title + "Comparing Run X (date) vs Run Y (date)"
//   - Headline metrics — 4-up grid, baseline value | delta | candidate value
//   - Per-model breakdown — table, one row per model, deltas per metric
//   - Reasoning variable changes — table sorted by absolute delta (biggest movers first)
//   - Name variant share changes — small table

import React, { useEffect, useState } from "react";
import {
  ChevronLeft, ArrowUp, ArrowDown, Minus, GitCompare, Loader2, AlertCircle,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

import {
  compareRuns,
  type RunComparison, type CustomerDetail, type RunSummary, type ResponseHealth,
  type LaneVersionComparability,
} from "@/api/onboarding-api";

import { getModelFullLabel } from "@/app/constants/models";
import { isIncomplete } from "@/api/run-completeness";
import { IncompleteRunNotice } from "@/app/components/shared/incomplete-run-notice";
import { RebaselineNotice } from "@/app/components/shared/rebaseline-notice";
import { toCount } from "./onboarding-batch-config";


// ───────────────────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────────────────

function fmtPct(v: number | null | undefined, decimals = 1): string {
  if (v == null) return "—";
  return `${(v * 100).toFixed(decimals)}%`;
}

function fmtPctDelta(v: number, decimals = 1): string {
  const sign = v > 0 ? "+" : "";
  return `${sign}${(v * 100).toFixed(decimals)}%`;
}

function fmtScore(v: number | null | undefined): string {
  if (v == null) return "—";
  return `${v.toFixed(2)} / 2`;
}

function fmtScoreDelta(v: number): string {
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(2)}`;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

const METRIC_LABELS: Record<string, string> = {
  mention_rate: "Mention rate",
  top5_rate: "Top-5 rate",
  primary_rec_rate: "Primary recommendation",
  number_one_rate: "#1 recommendation",
};


// ───────────────────────────────────────────────────────────────────────────
// Main component
// ───────────────────────────────────────────────────────────────────────────

interface CompareRunsPageProps {
  customer: CustomerDetail;
  baselineRunId: number;
  candidateRunId: number;
  /** All of the customer's runs — powers the baseline-run selector. */
  runs?: RunSummary[];
  onBack: () => void;
}

/** Short, identifiable label for a run in the baseline dropdown — includes the
 *  model set so a GPTweb-only backfill run is easy to spot. */
function runOptionLabel(run: RunSummary): string {
  const seq = run.run_sequence_number;
  const auto = (seq ?? 0) === 1 ? "Baseline run" : `Run #${seq ?? "?"}`;
  const name = (run.run_name ?? "").trim();
  // Show the custom name with the sequence kept as a reference suffix.
  const title = name ? `${name} (${auto})` : auto;
  const models = run.batch_config?.models || [];
  const modelStr = models.length ? ` · ${models.join(", ")}` : "";
  const incompleteStr = isIncomplete(run.completeness) ? " · incomplete" : "";
  // Series segment, so a pick across a rebaseline is visible in the picker.
  const seriesStr = run.series && run.series.segment > 0
    ? (run.series.new_baseline ? ` · new baseline (v${run.series.battery_version})` : ` · battery v${run.series.battery_version}`)
    : "";
  return `${title}${modelStr}${incompleteStr}${seriesStr}`;
}

export function CompareRunsPage({
  customer, baselineRunId, candidateRunId, runs = [], onBack,
}: CompareRunsPageProps) {
  const [data, setData] = useState<RunComparison | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The baseline run is user-selectable: the entry point pins it to the
  // sequence-1 run, but comparing a GPTweb signal needs to retarget the
  // baseline to the dedicated GPTweb backfill run.
  const [baselineId, setBaselineId] = useState(baselineRunId);
  useEffect(() => { setBaselineId(baselineRunId); }, [baselineRunId]);

  // Candidate is fixed; offer every other completed, reported run as a baseline.
  const baselineOptions = runs.filter(
    (r) => r.id !== candidateRunId && r.status === "completed" && r.has_report,
  );

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await compareRuns(customer.id, baselineId, candidateRunId);
      setData(res);
    } catch (e: any) {
      setError(e?.message || "Failed to load comparison");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer.id, baselineId, candidateRunId]);

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "var(--background)" }}
      >
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--alpha-teal)" }} />
          <span style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>
            Loading comparison…
          </span>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "var(--background)" }}
      >
        <div className="text-center">
          <AlertCircle className="w-8 h-8 mx-auto mb-3" style={{ color: "var(--infrared-coral)" }} />
          <p style={{ color: "var(--infrared-coral)", fontSize: "var(--text-sm)" }}>
            {error || "No comparison data available"}
          </p>
          <button
            onClick={onBack}
            className="mt-3 px-4 py-2 rounded-md"
            style={{
              backgroundColor: "var(--muted)", color: "var(--foreground)",
              fontSize: "var(--text-sm)", border: "1px solid var(--border)",
              cursor: "pointer",
            }}
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  const baseline = data.baseline_run;
  const candidate = data.candidate_run;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--background)" }}>
      <div className="w-full px-4 md:px-8 py-6 md:py-8">

        {/* Header */}
        <button
          onClick={onBack}
          className="flex items-center gap-2 mb-4 px-3 py-1.5 rounded-md"
          style={{
            background: "transparent",
            color: "var(--muted-foreground)",
            fontSize: "var(--text-sm)",
            border: "1px solid var(--border)",
            cursor: "pointer",
          }}
        >
          <ChevronLeft className="w-4 h-4" />
          Back to {customer.brand_name}
        </button>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-2">
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{
                backgroundColor: "var(--alpha-teal-10)",
                border: "1px solid var(--alpha-teal-25)",
              }}
            >
              <GitCompare className="w-5 h-5" style={{ color: "var(--alpha-teal)" }} />
            </div>
            <div>
              <div style={{
                fontSize: 11, color: "var(--alpha-teal)",
                fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em",
                marginBottom: 2,
              }}>
                Compare runs · {customer.brand_name}
              </div>
              <h1 style={{
                fontSize: "var(--text-xl)", fontWeight: 700,
                color: "var(--foreground)", margin: 0,
              }}>
                Run #{baseline.sequence_number} → Run #{candidate.sequence_number}
              </h1>
              <p style={{
                fontSize: "var(--text-xs)", color: "var(--muted-foreground)",
                marginTop: 4, margin: 0,
              }}>
                {fmtDate(baseline.completed_at)} → {fmtDate(candidate.completed_at)}
              </p>
            </div>
          </div>
          <button
            onClick={load}
            className="flex items-center gap-2 px-3 py-1.5 rounded-md flex-shrink-0"
            style={{
              backgroundColor: "var(--muted)", color: "var(--foreground)",
              fontSize: "var(--text-xs)", fontWeight: 500,
              border: "1px solid var(--border)", cursor: "pointer",
            }}
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </button>
        </div>

        {/* Baseline-run selector — lets the user retarget which run is the
            baseline (e.g. a dedicated GPTweb backfill run). */}
        {baselineOptions.length > 1 && (
          <div className="flex items-center gap-2 mt-4">
            <label
              htmlFor="compare-baseline-select"
              style={{
                fontSize: 11, color: "var(--muted-foreground)",
                fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em",
              }}
            >
              Baseline run
            </label>
            <select
              id="compare-baseline-select"
              value={baselineId}
              onChange={(e) => setBaselineId(Number(e.target.value))}
              style={{
                fontSize: "var(--text-sm)",
                color: "var(--foreground)",
                backgroundColor: "var(--card)",
                border: "1px solid var(--border)",
                borderRadius: 6,
                padding: "6px 10px",
                cursor: "pointer",
                maxWidth: "100%",
              }}
            >
              {baselineOptions.map((r) => (
                <option key={r.id} value={r.id}>
                  {runOptionLabel(r)}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Run config summary */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4 mb-6"
        >
          <RunConfigCard label={`Baseline (Run #${baseline.sequence_number})`} run={baseline} />
          <RunConfigCard label={`Candidate (Run #${candidate.sequence_number})`} run={candidate} />
        </div>

        {/* SERIES REBASELINE between the two runs: the comparison crosses a
            planned change in what is measured (battery, lanes, pins, rubric).
            It still compares, but says so and lists everything that changed.
            Nothing renders when both runs are on the same segment. */}
        {data.rebaseline?.crosses && data.rebaseline.crossed.map((rebaseline) => (
          <RebaselineNotice
            key={rebaseline.id}
            rebaseline={rebaseline}
            title={
              `This comparison crosses a series rebaseline (battery v${data.rebaseline!.baseline_battery_version}`
              + ` → v${data.rebaseline!.candidate_battery_version})`
            }
          />
        ))}

        {/* BATTERY NOTICE (#137). The two runs asked a different question
            battery and no series rebaseline between them covers the change, so
            every delta on this page is withheld. */}
        {data.battery && !data.battery.comparable && (
          <div
            className="flex items-start gap-2 mt-4 mb-2"
            style={{
              fontSize: "var(--text-sm)",
              color: "var(--foreground)",
              backgroundColor: "var(--muted)",
              border: "1px solid var(--infrared-coral)",
              borderRadius: 8,
              padding: "10px 12px",
            }}
          >
            <AlertCircle className="w-4 h-4 shrink-0" style={{ marginTop: 2, color: "var(--infrared-coral)" }} />
            <div>
              <div style={{ fontWeight: 600 }}>Not comparable: these runs asked different questions</div>
              <div style={{ color: "var(--muted-foreground)", marginTop: 2 }}>
                The question battery changed between them (battery {(data.battery.baseline_hash ?? "").slice(0, 8)} →{" "}
                {(data.battery.candidate_hash ?? "").slice(0, 8)}) and no series rebaseline covers the change, so every
                delta is withheld. Plan the change as a rebaseline to compare across it.
              </div>
            </div>
          </div>
        )}

        {/* MODEL-VERSION NOTICE (#114). A headline with every delta blanked is
            indistinguishable from a broken page, so name the cause above it.
            Shown for explained moves too: a delta that stands BECAUSE a forced
            platform move is logged still has part of its step attributable to
            the model, and the reader is entitled to know that before quoting
            the number. */}
        {data.model_versions && data.model_versions.changed_lanes.length > 0 && (
          <div
            className="flex items-start gap-2 mt-4 mb-2"
            style={{
              fontSize: "var(--text-sm)",
              color: "var(--foreground)",
              backgroundColor: "var(--muted)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              padding: "10px 12px",
            }}
          >
            <AlertCircle
              className="w-4 h-4 shrink-0"
              style={{ marginTop: 2, color: "var(--muted-foreground)" }}
            />
            <div>
              <div style={{ fontWeight: 600 }}>
                {data.model_versions.headline_comparable
                  ? "A lane changed model between these runs"
                  : "Headline deltas withheld: a lane changed model between these runs"}
              </div>
              <ul style={{ margin: "4px 0 0", paddingLeft: 16, color: "var(--muted-foreground)" }}>
                {data.model_versions.changed_lanes.map((lane) => {
                  const v = data.model_versions?.lanes?.[lane];
                  return (
                    <li key={lane}>
                      <strong>{getModelFullLabel(lane, lane)}</strong>
                      {": "}
                      {v?.baseline_version ?? "?"} → {v?.candidate_version ?? "?"}
                      {" — "}
                      {v?.rebaseline_id != null
                        ? "changed by the series rebaseline listed above (planned and logged)."
                        : v?.pin_move_logged
                        ? "logged platform model change; delta stands and is comparable."
                        : v?.reason === "operator_version_override"
                        ? "one run was pinned to another model by hand, so it is not a point on this brand's series."
                        : "no pin move logged for this change, so the delta is withheld."}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}

        {/* Headline metric deltas */}
        <Section title="Headline metrics">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
            {(["mention_rate", "top5_rate", "primary_rec_rate", "number_one_rate"] as const).map((metric) => {
              const d = data.headline_deltas[metric];
              if (!d) return null;
              return (
                <HeadlineMetricCard
                  key={metric}
                  label={METRIC_LABELS[metric]}
                  baseline={d.baseline}
                  candidate={d.candidate}
                  delta={d.delta}
                />
              );
            })}
          </div>
          {/* Either side incomplete: its rates are over fewer answered calls
              than it paid for. Nothing renders when both are complete. */}
          {isIncomplete(data.baseline_run.completeness) && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600 }}>Baseline run</div>
              <IncompleteRunNotice completeness={data.baseline_run.completeness} />
            </div>
          )}
          {isIncomplete(data.candidate_run.completeness) && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600 }}>Candidate run</div>
              <IncompleteRunNotice completeness={data.candidate_run.completeness} />
            </div>
          )}
        </Section>

        {/* Per-model breakdown */}
        <Section title="Per-model breakdown">
          <DeltaTable
            headers={["Model", "Mention", "Top-5", "Primary rec", "#1 rec"]}
            colWidths={["28%", "18%", "18%", "18%", "18%"]}
            rows={Object.entries(data.per_model_deltas).map(([model, metrics]) => ({
              key: model,
              cells: [
                <ModelCoverageCell
                  label={getModelFullLabel(model, model)}
                  inBaseline={metrics.in_baseline !== false}
                  inCandidate={metrics.in_candidate !== false}
                  health={metrics.candidate_health}
                />,
                <DeltaPair {...metrics.mention_rate} version={metrics.version} />,
                <DeltaPair {...metrics.top5_rate} version={metrics.version} />,
                <DeltaPair {...metrics.primary_rec_rate} version={metrics.version} />,
                <DeltaPair {...metrics.number_one_rate} version={metrics.version} />,
              ],
            }))}
          />
        </Section>

        {/* Reasoning variable changes */}
        <Section title="Reasoning variable changes (sorted by biggest movers)">
          <DeltaTable
            headers={["Variable", "Claim", "Baseline", "Candidate", "Delta"]}
            colWidths={["10%", "44%", "14%", "14%", "18%"]}
            rows={data.rv_deltas.map((rv) => ({
              key: rv.rv_id,
              cells: [
                <span style={{ fontWeight: 700, fontFamily: "monospace", fontSize: 13 }}>
                  {rv.rv_id}
                </span>,
                <span style={{ fontSize: "var(--text-sm)" }}>
                  {rv.claim}
                </span>,
                fmtScore(rv.baseline),
                fmtScore(rv.candidate),
                <ScoreDeltaCell delta={rv.delta} />,
              ],
            }))}
          />
        </Section>

        {/* Name variant share changes */}
        {data.variant_deltas.length > 0 && (
          <Section title="Name variant share changes">
            <DeltaTable
              headers={["Variant", "Baseline count", "Candidate count", "Baseline share", "Candidate share"]}
              colWidths={["32%", "17%", "17%", "17%", "17%"]}
              rows={data.variant_deltas.map((v) => ({
                key: v.variant,
                cells: [
                  v.variant,
                  String(v.baseline_count),
                  String(v.candidate_count),
                  fmtPct(v.baseline_share),
                  fmtPct(v.candidate_share),
                ],
              }))}
            />
          </Section>
        )}

      </div>
    </div>
  );
}


// ───────────────────────────────────────────────────────────────────────────
// Sub-components
// ───────────────────────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-6">
      <h2 style={{
        fontSize: "var(--text-sm)", fontWeight: 700, color: "var(--alpha-teal)",
        textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 12,
        margin: 0,
      }}>
        {title}
      </h2>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function RunConfigCard({
  label, run,
}: {
  label: string;
  run: RunComparison["baseline_run"];
}) {
  const cfg = run.batch_config || {};
  return (
    <div
      className="rounded-lg p-3"
      style={{
        backgroundColor: "var(--card)",
        border: "1px solid var(--border)",
      }}
    >
      <div style={{
        fontSize: 10, color: "var(--muted-foreground)",
        fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em",
        marginBottom: 4,
      }}>
        {label}
      </div>
      <p style={{
        fontSize: "var(--text-sm)", color: "var(--foreground)",
        margin: 0, fontWeight: 500,
      }}>
        {toCount(cfg.queries, 0)} queries × {toCount(cfg.reps, 0)} reps × {(cfg.models || []).length} models
        = {cfg.total_calls ?? "?"} calls
      </p>
      <p style={{
        fontSize: "var(--text-xs)", color: "var(--muted-foreground)",
        margin: 0, marginTop: 2,
      }}>
        Completed {fmtDate(run.completed_at)}
      </p>
    </div>
  );
}


function HeadlineMetricCard({
  label, baseline, candidate, delta,
}: {
  label: string;
  baseline: number;
  candidate: number;
  delta: number | null;
}) {
  // A null delta means the two runs did not blend the same lanes. Math.abs(null)
  // is 0 in JS, so without this the card would quietly render "no change",
  // which is a stronger and more wrong claim than showing nothing.
  const notComparable = delta === null || delta === undefined;
  const hasDelta = !notComparable && Math.abs(delta as number) > 0.0005;
  const isUp = !notComparable && (delta as number) > 0;

  return (
    <div
      className="rounded-lg p-3"
      style={{
        backgroundColor: "var(--card)",
        border: "1px solid var(--border)",
      }}
    >
      <div style={{
        fontSize: 10, color: "var(--muted-foreground)",
        fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em",
        marginBottom: 6,
      }}>
        {label}
      </div>
      <div className="flex items-baseline gap-2 mb-1">
        <span style={{
          fontSize: "var(--text-2xl)", fontWeight: 700,
          color: "var(--foreground)",
        }}>
          {fmtPct(candidate)}
        </span>
        {hasDelta && (
          <span
            className="flex items-center gap-0.5"
            style={{
              fontSize: 12, fontWeight: 700,
              color: isUp ? "var(--chart-3)" : "var(--infrared-coral)",
            }}
          >
            {isUp
              ? <ArrowUp className="w-3.5 h-3.5" />
              : <ArrowDown className="w-3.5 h-3.5" />}
            {fmtPctDelta(delta as number)}
          </span>
        )}
      </div>
      <div style={{
        fontSize: "var(--text-xs)", color: "var(--muted-foreground)",
      }}>
        {notComparable
          ? `was ${fmtPct(baseline)}, not directly comparable (different models measured)`
          : `was ${fmtPct(baseline)}`}
      </div>
    </div>
  );
}


function ModelCoverageCell({
  label, inBaseline, inCandidate, health,
}: {
  label: string;
  inBaseline: boolean;
  inCandidate: boolean;
  health?: ResponseHealth | null;
}) {
  // Flag models that can't be compared directly: added this run (no baseline)
  // or dropped this run (in baseline only). Comparable models render the label
  // alone.
  const note = !inBaseline
    ? "new — no baseline"
    : !inCandidate
    ? "not in this run"
    : null;

  // Collection-health flag: a 0% backed by empty/errored responses is a data
  // problem, not a real signal. Red when the model produced no usable answer at
  // all this run; amber when some calls came back blank/errored.
  const bad = (health?.empty ?? 0) + (health?.errored ?? 0);
  const noUsableAnswers = !!health && health.total > 0 && health.answered === 0;
  const healthNote =
    health && bad > 0
      ? health.errored > 0
        ? `${health.errored}/${health.total} errored`
        : `${health.empty}/${health.total} blank`
      : null;
  const healthTooltip =
    health?.sample_error
      ? `First error: ${health.sample_error}`
      : noUsableAnswers
      ? "Every response was blank — model returned no text, so the 0% is missing data, not a real result."
      : "Some responses were blank or errored.";

  return (
    <span className="flex items-center gap-2 flex-wrap">
      <span>{label}</span>
      {note && (
        <span
          style={{
            fontSize: 10, fontWeight: 600,
            color: "var(--muted-foreground)",
            backgroundColor: "var(--muted)",
            border: "1px solid var(--border)",
            borderRadius: 4, padding: "1px 6px",
            whiteSpace: "nowrap",
          }}
        >
          {note}
        </span>
      )}
      {healthNote && (
        <span
          title={healthTooltip}
          style={{
            fontSize: 10, fontWeight: 700,
            color: noUsableAnswers ? "var(--infrared-coral)" : "var(--chart-3)",
            backgroundColor: noUsableAnswers ? "rgba(220,38,38,0.10)" : "rgba(245,158,11,0.10)",
            border: `1px solid ${noUsableAnswers ? "var(--infrared-coral)" : "var(--chart-3)"}`,
            borderRadius: 4, padding: "1px 6px",
            whiteSpace: "nowrap", cursor: "help",
          }}
        >
          ⚠ {healthNote}
        </span>
      )}
    </span>
  );
}


function DeltaPair({
  baseline, candidate, delta, version,
}: {
  baseline: number | null;
  candidate: number | null;
  delta: number | null;
  version?: LaneVersionComparability | null;
}) {
  // A MODEL-VERSION refusal (#114) is not a coverage gap: the lane ran in both
  // runs and both values are real, so show BOTH and suppress only the arrow.
  // This case has to be handled before the coverage branch below — it arrives
  // as delta === null with a non-null baseline, which that branch would label
  // "new" and render as a single value, hiding a measurement that exists and
  // claiming a lane is new when it has run all along.
  const versionRefused =
    delta === null && baseline !== null && candidate !== null
    && version?.changed === true && version?.comparable === false;
  if (versionRefused) {
    return (
      <div className="flex items-center justify-end gap-2">
        <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
          {fmtPct(baseline)} →
        </span>
        <span style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>
          {fmtPct(candidate)}
        </span>
        <span
          title={
            `Ran on ${version?.baseline_version ?? "?"} then `
            + `${version?.candidate_version ?? "?"}. `
            + (version?.reason === "operator_version_override"
              ? "One of these runs was pinned to another model by hand, so it is not a point on this brand's series."
              : "No pin move is logged for that change, so the step is part model change and part brand movement.")
          }
          style={{ fontSize: 10, color: "var(--muted-foreground)", fontStyle: "italic" }}
        >
          model changed
        </span>
      </div>
    );
  }

  // No comparable baseline (model added this run, e.g. gptweb/Gemini) → show the
  // candidate value only, tagged, instead of a misleading lift vs zero. If the
  // candidate side is missing instead, the model was dropped this run.
  if (delta === null || baseline === null) {
    return (
      <div className="flex items-center justify-end gap-2">
        <span style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>
          {fmtPct(candidate)}
        </span>
        <span style={{ fontSize: 10, color: "var(--muted-foreground)", fontStyle: "italic" }}>
          {candidate === null ? "not run" : "new"}
        </span>
      </div>
    );
  }

  const hasDelta = Math.abs(delta) > 0.0005;
  const isUp = delta > 0;

  return (
    <div className="flex items-center justify-end gap-2">
      <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
        {fmtPct(baseline)} →
      </span>
      <span style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>
        {fmtPct(candidate)}
      </span>
      {hasDelta ? (
        <span
          className="flex items-center"
          style={{
            fontSize: 11, fontWeight: 700,
            color: isUp ? "var(--chart-3)" : "var(--infrared-coral)",
          }}
        >
          {isUp
            ? <ArrowUp className="w-3 h-3" />
            : <ArrowDown className="w-3 h-3" />}
          {fmtPctDelta(Math.abs(delta))}
        </span>
      ) : (
        <span style={{
          fontSize: 11, color: "var(--muted-foreground)",
          display: "flex", alignItems: "center",
        }}>
          <Minus className="w-3 h-3" />
        </span>
      )}
    </div>
  );
}


function ScoreDeltaCell({ delta }: { delta: number | null }) {
  // Null: not comparable (a battery change no rebaseline covers, #137).
  if (delta === null || delta === undefined) {
    return (
      <span className="flex items-center justify-end gap-1" style={{ fontSize: 11, color: "var(--muted-foreground)" }}>
        Not comparable
      </span>
    );
  }
  const hasDelta = Math.abs(delta) > 0.005;
  const isUp = delta > 0;

  if (!hasDelta) {
    return (
      <span
        className="flex items-center justify-end gap-1"
        style={{ fontSize: 11, color: "var(--muted-foreground)" }}
      >
        <Minus className="w-3 h-3" /> No change
      </span>
    );
  }

  return (
    <span
      className="flex items-center justify-end gap-1"
      style={{
        fontSize: "var(--text-sm)", fontWeight: 700,
        color: isUp ? "var(--chart-3)" : "var(--infrared-coral)",
      }}
    >
      {isUp ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />}
      {fmtScoreDelta(delta)}
    </span>
  );
}


function DeltaTable({
  headers, rows, colWidths,
}: {
  headers: string[];
  rows: Array<{ key: string | number; cells: React.ReactNode[] }>;
  colWidths?: string[];
}) {
  if (rows.length === 0) {
    return (
      <p style={{
        fontSize: "var(--text-sm)", color: "var(--muted-foreground)",
        textAlign: "center", padding: "16px 12px",
      }}>
        No data to display.
      </p>
    );
  }

  return (
    <div
      className="rounded-lg overflow-hidden"
      style={{
        backgroundColor: "var(--card)",
        border: "1px solid var(--border)",
      }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
        <colgroup>
          {(colWidths || headers.map(() => `${100 / headers.length}%`)).map((w, i) => (
            <col key={i} style={{ width: w }} />
          ))}
        </colgroup>
        <thead>
          <tr style={{ backgroundColor: "var(--muted)" }}>
            {headers.map((h, idx) => (
              <th
                key={idx}
                style={{
                  padding: "10px 12px",
                  fontSize: 11, fontWeight: 700,
                  color: "var(--muted-foreground)",
                  textAlign: idx === 0 || idx === 1 ? "left" : "right",
                  textTransform: "uppercase", letterSpacing: "0.04em",
                  borderBottom: "1px solid var(--border)",
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIdx) => (
            <tr
              key={row.key}
              style={{
                borderBottom: rowIdx < rows.length - 1 ? "1px solid var(--border)" : "none",
              }}
            >
              {row.cells.map((cell, idx) => (
                <td
                  key={idx}
                  style={{
                    padding: "10px 12px",
                    fontSize: "var(--text-sm)",
                    color: "var(--foreground)",
                    textAlign: idx === 0 || idx === 1 ? "left" : "right",
                    verticalAlign: "middle",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}